import { useAddClientAction } from "@components/organizationView/clients/useAddClientAction";
import { useActiveOrganization } from "@hooks/useActiveOrganization";
import { PlusIcon } from "@icons";
import { Command } from "@vendor/cmdk/cmdk";
import { FC, useCallback } from "react";
import { useOverlays } from "contexts/OverlayContext";

import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useCanPerformOrganizationAction } from "@hooks/permissions/organization/useCanPerformOrganizationAction";
import { OrganizationAction } from "@common/userOrganizationRoles";
import { CommandItem } from "@design-system/CommandItem/CommandItem";
import { useCommands } from "./useCommands";

export const OrganizationActionsCommands: FC = () => {
    const addClientAction = useAddClientAction();
    const { openOverlay } = useOverlays();
    const { close: closeCommands } = useCommands();
    const { toast } = useToaster();
    const activeOrganization = useActiveOrganization();

    const canCreateClient = useCanPerformOrganizationAction(
        OrganizationAction.CREATE_CLIENT,
    );
    const canAddTeamMember = useCanPerformOrganizationAction(
        OrganizationAction.INVITE_TEAM_MEMBER,
    );

    const onAddTeamMember = useCallback(() => {
        if (!activeOrganization) {
            return;
        }
        openOverlay("organizationAddTeamMember", {
            organization: activeOrganization,
        });
        closeCommands();
    }, [activeOrganization, closeCommands, openOverlay]);

    const onAddClient = useCallback(() => {
        if (
            addClientAction.isEnrollmentRequired &&
            addClientAction.isDisabled
        ) {
            toast(addClientAction.disabledReason);
        } else {
            addClientAction.addClient();
        }
        closeCommands();
    }, [addClientAction, closeCommands, toast]);

    if (![canCreateClient, canAddTeamMember].some(Boolean)) {
        return null;
    }

    return (
        <Command.Group heading="Organization">
            {canCreateClient && (
                <CommandItem
                    keywords={["workspace", "new", "create"]}
                    onSelect={onAddClient}
                    shortcut="c"
                >
                    {addClientAction.isEnrollmentRequired ? null : <PlusIcon />}
                    Add client
                </CommandItem>
            )}

            {canAddTeamMember && (
                <CommandItem
                    keywords={["member", "team"]}
                    onSelect={onAddTeamMember}
                    shortcut="t"
                >
                    <PlusIcon /> Add member
                </CommandItem>
            )}
        </Command.Group>
    );
};
